import React, { createContext, useCallback, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { profileApi } from '../services/api';
import { clearUserScopedSession } from '../lib/session';
import { stopPush } from '../native/push';
import { isNative } from '../native/platform';
import { tokenTag } from '../lib/persistCache';
import { clientPlatform } from '../lib/clientPlatform';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

// The desktop app's own windows (src/desktop/) stay on their page: no redirects there.
const onDesktopSurface = () => typeof window !== 'undefined' && window.location.pathname.startsWith('/desktop/');

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// The phone app keeps who is signed in, so it opens straight into the app (no
// spinner while /auth/me answers) and stays signed in without a connection.
const USER_KEY = 'bb.user';
const cachedUser = (token) => {
  if (!isNative() || !token) return null;
  try {
    const saved = JSON.parse(localStorage.getItem(USER_KEY) || 'null');
    return saved && saved.tag === tokenTag(token) ? saved.user : null;
  } catch {
    return null;
  }
};
const keepUser = (token, user) => {
  if (!isNative()) return;
  try { localStorage.setItem(USER_KEY, JSON.stringify({ tag: tokenTag(token), user })); } catch { /* full */ }
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUserState] = useState(() => cachedUser(localStorage.getItem('token')));
  const [loading, setLoading] = useState(() => !cachedUser(localStorage.getItem('token')));
  const setUser = useCallback((next) => {
    setUserState(next);
    const current = localStorage.getItem('token');
    if (next && current) keepUser(current, next);
  }, []);

  const logout = useCallback((redirectTo = '/') => {
    stopPush(localStorage.getItem('token')); // the app: this phone gets no more pushes
    localStorage.removeItem('token');
    clearUserScopedSession();
    setToken(null);
    setUser(null);
    // Hard reload guarantees that root-level providers (TaskContext,
    // DateTimeContext, UndoContext) and the TanStack Query cache are torn
    // down. Without this, the next account that logs in on the same browser
    // sees the previous user's cached data on first render.
    if (typeof window !== 'undefined') {
      if (onDesktopSurface()) window.location.reload();
      else window.location.href = redirectTo;
    }
  }, [setUser]);

  // The user of this token. A retry or an answer for a token that is no longer the
  // current one (signed out, another account) is dropped.
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    let retry = null;
    const load = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!cancelled) setUser(response.data);
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch user:', error);
        // The desktop app starts with the computer, often before the network is up:
        // there an unreachable server is tried again; a refused token still signs out.
        if (onDesktopSurface() && !error.response) {
          retry = setTimeout(load, 10000);
          return;
        }
        // The phone app without a connection stays signed in with the user it knows.
        if (!error.response && cachedUser(token)) return;
        logout();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
      clearTimeout(retry);
    };
  }, [token, setUser, logout]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    // MFA path: caller must collect a 6-digit code, then call finalizeSession
    // with the access_token returned by /api/auth/mfa/challenge.
    if (response.data.mfa_required) {
      return response.data;
    }
    await finalizeSession(response.data.access_token);
    return response.data;
  };

  /**
   * Install a fresh access token + load /me. Shared by password login,
   * magic-link consume, OAuth handoff, and MFA challenge completion - any
   * code path that ends with a JWT in hand.
   */
  const finalizeSession = async (accessToken) => {
    localStorage.setItem('token', accessToken);
    setToken(accessToken);
    const userResponse = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${accessToken}` }
    });
    setUser(userResponse.data);
  };

  /** `extra`: the sign-up page's quiet bot checks (website, form_ms) and where the person came
   * from (source), see backend utils/signup_guard.py. */
  const register = async (email, password, fullName, extra = {}) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      full_name: fullName,
      ...extra,
    }, { headers: { 'X-Client-Platform': clientPlatform() } });
    const { access_token } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    // Fetch user immediately
    const userResponse = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${access_token}` }
    });
    setUser(userResponse.data);
    return response.data;
  };

  const refreshUser = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch {
      // silently ignore
    }
  };

  const updateProfile = async (data) => {
    await profileApi.updateProfile(data);
    await refreshUser();
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    refreshUser,
    updateProfile,
    finalizeSession,
    isAuthenticated: !!token && !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
